import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../app/store';
import type { LEDBackgroundType } from '../screens/LEDScreen';
import type { LowerThirdPosition, BroadcastBackgroundType, BroadcastFloorOrder } from '../screens/BroadcastScreen';
import type { VisualContentType } from '../screens/visual/types';
import type { BreakingBracketView } from './breakingBracketSlice';

/**
 * Target for visual control commands
 */
export type VisualTarget = 'led' | 'broadcast' | 'both';

/**
 * Visual Control Command - sent from controller to LED/Broadcast
 */
export interface VisualControlCommand {
  target: VisualTarget;
  contentType: VisualContentType;
  visible: boolean;
  // Content data (participant, result, etc.)
  data?: VisualContentData;
  // Background settings (LED)
  background?: {
    type: LEDBackgroundType;
    color?: string;
    imageUrl?: string;
    videoUrl?: string;
  };
  // Background settings (Broadcast)
  broadcastBackground?: {
    type: BroadcastBackgroundType;
    color?: string;
    comboMode?: boolean;
  };
  // Lower third position (Broadcast only)
  lowerThirdPosition?: LowerThirdPosition;
  // Theme ID (shared by LED + Broadcast — one theme per competition)
  themeId?: string;
  // Floor A position for joined two-floor heats (Broadcast only)
  floorOrder?: BroadcastFloorOrder;
  // Which way round the Breaking battle board faces on the LED
  ledBattleSides?: LedBattleSides;
  // Auto-heats mode toggle
  autoHeatsEnabled?: boolean;
}

/**
 * Which way round the LED's Breaking battle board faces. The judges' phones put
 * blue on the left, as the judges see the floor; a wall hung behind the judges
 * faces the hall, which sees it the other way round - so 'audience' (red left)
 * is the default and 'judges' is for a wall in front of the panel.
 */
export type LedBattleSides = 'audience' | 'judges';

/**
 * A single category's current-heat data for the joined heats display.
 */
export interface JoinedHeatCategory {
  floor: 'one' | 'A' | 'B';
  categoryName: string;   // e.g. "Junior I ST"
  roundName: string;      // e.g. "1st round"
  danceName: string;      // e.g. "Samba"
  heatNo: number;         // current danceHeatNo
  totalHeats: number;     // number of heats in this dance
  participants: number[]; // current-heat start numbers (sorted)
}

/**
 * Content data that can be displayed
 */
export interface VisualContentData {
  // Single participant/result
  participant?: {
    startNumber: number;
    name: string;
    country: string;
    flagUrl?: string;
    customFlag?: string;
  };
  // Couple (for partner dances)
  couple?: {
    startNumber: number;
    leader: string;
    follower: string;
    country: string;
    club?: string;
    flagUrl?: string;
    customFlag?: string;
  };
  // Results data
  result?: {
    place: number;
    scores?: Record<string, number | string>;
    // Pre-formatted total rendered as a dedicated "SCORE" (kept out of `scores`
    // so it isn't drawn as a criteria token). Absent when no total should show.
    score?: string;
    dances?: string[];
    // AJS27: one entry per adjudicator, ordered by letter. Rendered as a strip
    // of flags with the score beneath, ski-jumping style, in place of the
    // criteria line - AJS27 merges a judge's criteria before anything else, so
    // the judge is the meaningful unit on screen, not the criterion.
    judgeScores?: Array<{
      letter: string;
      country: string;
      customFlag?: string;
      score: number;
      /** Struck as one of the extremes; drawn dimmed and struck through. */
      rejected: boolean;
      /** The draw picked this adjudicator out of several with equal scores. */
      byDraw: boolean;
    }>;
  };
  // Start list / Heat data
  participants?: Array<{
    startNumber: number;
    name?: string;
    country?: string;
    flagUrl?: string;
    customFlag?: string;
  }>;
  // Round info
  round?: {
    name: string;
    categoryName: string;
    dances?: string[];
  };
  // Competition info
  competition?: {
    city: string;
    country: string;
  };
  // Adjudicator(s)
  adjudicator?: {
    letter: string;
    name: string;
    country?: string;
    role?: string; // 'Judge', 'Chairperson', 'Scrutineer'
  };
  adjudicators?: Array<{
    letter: string;
    name: string;
    country?: string;
    role?: string;
  }>;
  // Podium data
  podium?: {
    gold: { name: string; country: string; flagUrl?: string; customFlag?: string };
    silver?: { name: string; country: string; flagUrl?: string; customFlag?: string };
    bronze?: { name: string; country: string; flagUrl?: string; customFlag?: string };
  };
  // Heat numbers
  heats?: {
    current: number;
    next?: number;
    currentParticipants: number[];
    nextParticipants?: number[];
    nextDanceName?: string;
  };
  // Joined-categories heats (one-floor stacked or two-floor split).
  // Additive/optional — the single-category `heats` field is untouched.
  joinedHeats?: {
    mode: 'one-floor' | 'two-floor';
    categories: JoinedHeatCategory[];
  };
  // Current dance info
  currentDance?: {
    danceName: string;
    danceNumber?: number;
    totalDances?: number;
  };
  // Solo participant
  soloParticipant?: {
    startNumber: number;
    name: string;
    country: string;
    club?: string;
    flagUrl?: string;
    customFlag?: string;
  };
  // Officials list
  officials?: Array<{
    role: string;
    name: string;
    country?: string;
    letter?: string;
  }>;
  // Results list (for dance-results / total-results screens)
  results?: Array<{
    place: number;
    startNumber?: number;
    name: string;
    country?: string;
    scores?: Record<string, number | string>;
    score?: string;
    customFlag?: string;
  }>;
  // Country flag (full screen display)
  countryFlag?: {
    country: string;
    flagUrl?: string;
    customFlag?: string;
  };
  // Breaking battle live status board (adjudicator cards). Cells arrive already
  // resolved to a display state, so the LED only paints colours.
  breakingBattle?: BreakingBattleBoard;
  // Breaking knockout bracket, as pushed to the phones - see
  // breakingBracketSlice. Fully resolved on the desktop.
  breakingBracket?: BreakingBracketView;
  // Flag wave animation
  waveEffect?: boolean;
}

/** One adjudicator's card in a battle round. */
export type BreakingCellState = 'pending' | 'submitted' | 'red' | 'blue';

export interface BreakingBattleAthlete {
  name: string;
  country?: string;
  flagUrl?: string;
  customFlag?: string;
  /** Battle rounds won across the revealed rounds. */
  roundsWon: number;
}

export interface BreakingBattleBoard {
  categoryName: string;
  phaseLabel: string;
  red: BreakingBattleAthlete;
  blue: BreakingBattleAthlete;
  adjudicators: Array<{ uuid: string; label: string }>;
  rounds: Array<{
    index: number;
    revealed: boolean;
    cells: Record<string, BreakingCellState>;
  }>;
}

/**
 * Current visual state - synced across all clients
 */
export interface VisualState {
  // Auto-heats mode: MC/Chairperson devices auto-send next heat on completion
  autoHeatsEnabled: boolean;
  // Active theme ID — one theme per competition, shared by LED and Broadcast
  themeId: string;
  // LED Screen state
  led: {
    visible: boolean;
    contentType: VisualContentType;
    data?: VisualContentData;
    background: {
      type: LEDBackgroundType;
      color: string;
      imageUrl?: string;
      videoUrl?: string;
    };
    // Breaking battle board orientation - see LedBattleSides
    battleSides?: LedBattleSides;
  };
  // Broadcast state
  broadcast: {
    visible: boolean;
    contentType: VisualContentType;
    data?: VisualContentData;
    lowerThirdPosition: LowerThirdPosition;
    // Floor A position for the joined two-floor heats layout
    floorOrder: BroadcastFloorOrder;
    background: {
      type: BroadcastBackgroundType;
      color: string;
      comboMode?: boolean;
    };
  };
}

const initialState: VisualState = {
  autoHeatsEnabled: false,
  themeId: 'wdsf-gold',
  led: {
    visible: false,
    contentType: 'idle',
    background: {
      type: 'solid-color',
      color: '#000000',
    },
    battleSides: 'audience',
  },
  broadcast: {
    visible: false,
    contentType: 'idle',
    lowerThirdPosition: 'bottom-left',
    floorOrder: 'a-left',
    background: {
      type: 'theme-background',
      color: '#000000',
      comboMode: false,
    },
  },
};

const visualSlice = createSlice({
  name: 'visual',
  initialState,
  reducers: {
    /**
     * Update the full visual state (received from server)
     */
    setVisualState(state, action: PayloadAction<VisualState>) {
      return action.payload;
    },

    /**
     * Apply a visual control command (updates local state)
     */
    applyVisualControl(state, action: PayloadAction<VisualControlCommand>) {
      const { target, contentType, visible, data, background, broadcastBackground, lowerThirdPosition, floorOrder } = action.payload;

      // Handle autoHeatsEnabled toggle
      if (action.payload.autoHeatsEnabled !== undefined) {
        state.autoHeatsEnabled = action.payload.autoHeatsEnabled;
      }
      // Auto-disable when content changes to non-heats (single or joined)
      if (contentType && contentType !== 'heats' && contentType !== 'heats-joined') {
        state.autoHeatsEnabled = false;
      }

      // Theme is shared by LED + Broadcast — applied regardless of target.
      if (action.payload.themeId) {
        state.themeId = action.payload.themeId;
      }

      if (target === 'led' || target === 'both') {
        state.led.visible = visible;
        state.led.contentType = contentType;
        if (data !== undefined) {
          state.led.data = data;
        }
        if (background) {
          state.led.background = {
            ...state.led.background,
            ...background,
          };
        }
        if (action.payload.ledBattleSides) {
          state.led.battleSides = action.payload.ledBattleSides;
        }
      }

      if (target === 'broadcast' || target === 'both') {
        state.broadcast.visible = visible;
        state.broadcast.contentType = contentType;
        if (data !== undefined) {
          state.broadcast.data = data;
        }
        if (lowerThirdPosition) {
          state.broadcast.lowerThirdPosition = lowerThirdPosition;
        }
        if (floorOrder) {
          state.broadcast.floorOrder = floorOrder;
        }
        if (broadcastBackground) {
          state.broadcast.background = {
            ...state.broadcast.background,
            ...broadcastBackground,
          };
        }
      }
    },

    /**
     * Set LED visibility
     */
    setLedVisible(state, action: PayloadAction<boolean>) {
      state.led.visible = action.payload;
    },

    /**
     * Set Broadcast visibility
     */
    setBroadcastVisible(state, action: PayloadAction<boolean>) {
      state.broadcast.visible = action.payload;
    },

    /**
     * Set LED content type
     */
    setLedContentType(state, action: PayloadAction<VisualContentType>) {
      state.led.contentType = action.payload;
    },

    /**
     * Set Broadcast content type
     */
    setBroadcastContentType(state, action: PayloadAction<VisualContentType>) {
      state.broadcast.contentType = action.payload;
    },

    /**
     * Set LED content data
     */
    setLedData(state, action: PayloadAction<VisualContentData | undefined>) {
      state.led.data = action.payload;
    },

    /**
     * Set Broadcast content data
     */
    setBroadcastData(state, action: PayloadAction<VisualContentData | undefined>) {
      state.broadcast.data = action.payload;
    },

    /**
     * Set LED background
     */
    setLedBackground(state, action: PayloadAction<VisualState['led']['background']>) {
      state.led.background = action.payload;
    },

    /**
     * Set Broadcast background
     */
    setBroadcastBackground(state, action: PayloadAction<VisualState['broadcast']['background']>) {
      state.broadcast.background = action.payload;
    },

    /**
     * Set the active theme (shared by LED + Broadcast)
     */
    setTheme(state, action: PayloadAction<string>) {
      state.themeId = action.payload;
    },

    /**
     * Set broadcast floor order (Floor A position for joined two-floor heats)
     */
    setBroadcastFloorOrder(state, action: PayloadAction<BroadcastFloorOrder>) {
      state.broadcast.floorOrder = action.payload;
    },

    /**
     * Set which way round the LED's Breaking battle board faces
     */
    setLedBattleSides(state, action: PayloadAction<LedBattleSides>) {
      state.led.battleSides = action.payload;
    },

    /**
     * Clear all visual outputs (go to idle)
     */
    clearAllVisuals(state) {
      state.autoHeatsEnabled = false;
      state.led.visible = false;
      state.led.contentType = 'idle';
      state.led.data = undefined;
      state.broadcast.visible = false;
      state.broadcast.contentType = 'idle';
      state.broadcast.data = undefined;
    },

    /**
     * Set auto-heats mode
     */
    setAutoHeats(state, action: PayloadAction<boolean>) {
      state.autoHeatsEnabled = action.payload;
    },
  },
});

// Export actions
export const {
  setVisualState,
  applyVisualControl,
  setLedVisible,
  setBroadcastVisible,
  setLedContentType,
  setBroadcastContentType,
  setLedData,
  setBroadcastData,
  setLedBackground,
  setBroadcastBackground,
  setTheme,
  setBroadcastFloorOrder,
  setLedBattleSides,
  clearAllVisuals,
  setAutoHeats,
} = visualSlice.actions;

// Selectors
export const selectVisualState = (state: RootState) => state.visual;
export const selectLedState = (state: RootState) => state.visual.led;
export const selectBroadcastState = (state: RootState) => state.visual.broadcast;
export const selectLedVisible = (state: RootState) => state.visual.led.visible;
export const selectBroadcastVisible = (state: RootState) => state.visual.broadcast.visible;
export const selectLedContentType = (state: RootState) => state.visual.led.contentType;
export const selectBroadcastContentType = (state: RootState) => state.visual.broadcast.contentType;
export const selectLedData = (state: RootState) => state.visual.led.data;
export const selectBroadcastData = (state: RootState) => state.visual.broadcast.data;
export const selectLedBackground = (state: RootState) => state.visual.led.background;
export const selectBroadcastBackground = (state: RootState) => state.visual.broadcast.background;
export const selectThemeId = (state: RootState) => state.visual.themeId ?? 'wdsf-gold';
export const selectBroadcastFloorOrder = (state: RootState) => state.visual.broadcast.floorOrder ?? 'a-left';
export const selectLedBattleSides = (state: RootState): LedBattleSides =>
  state.visual.led.battleSides ?? 'audience';
export const selectAutoHeatsEnabled = (state: RootState) => state.visual.autoHeatsEnabled;

export default visualSlice.reducer;
